import { camelCase, startsWith } from "lodash-es";
import { captureException } from "@sentry/react";
import type { ColumnDef, GroupColumnDef } from "@tanstack/react-table";
import type { SWRResponse } from "swr";

import type {
  GetAssetAttributeLookupDto,
  GetAssetCategoryLookupDto,
} from "contexts/configuration";

const SHARED_BASIC_ATTRIBUTES_CATEGORY_CODE = "WellAttributes";
const BASIC_ATTRIBUTES_SUBCATEGORY_CODE = "BasicAttributes";

export const mergeColumnDefs = <T>(
  categoryCode:
    | "WellAttributes"
    | "Risk"
    | "KeyLikelihoodFactors"
    | "KeyConsequenceFactors",
  columnDefs: Array<ColumnDef<T, unknown>>,
  publishedAttributesResponse: SWRResponse<
    Array<GetAssetCategoryLookupDto>,
    unknown,
    unknown
  >,
): Array<ColumnDef<T, unknown>> => {
  if (
    !publishedAttributesResponse.data ||
    publishedAttributesResponse.isLoading
  ) {
    return columnDefs;
  }

  const attributeMap = publishedAttributesResponse.data.find(
    (mapping) => mapping.code === categoryCode,
  );

  const sharedBasicAttributesMap = publishedAttributesResponse.data
    .find((mapping) => mapping.code === SHARED_BASIC_ATTRIBUTES_CATEGORY_CODE)
    ?.subcategories.find(
      (subcategory) => subcategory.code === BASIC_ATTRIBUTES_SUBCATEGORY_CODE,
    );

  if (!attributeMap) {
    captureException(
      new Error(`No attribute mapping found for ${categoryCode}`),
      {
        tags: {
          caller: "mergeColumnDefs",
        },
      },
    );

    return columnDefs;
  }

  const mergeCustomColumns = <T>(
    columns: Array<ColumnDef<T>>,
    attributes: Array<GetAssetAttributeLookupDto>,
    columnOptions?: Partial<ColumnDef<T>>,
  ) => {
    return columns.concat(
      attributes
        .filter((attribute) => startsWith(attribute.code, "Custom"))
        .map((atribute) => ({
          id: atribute.code,
          header: atribute.name,
          accessorKey: camelCase(atribute.code),
          ...columnOptions,
        })),
    );
  };

  const mapLeafColumns = <T>(
    columns: Array<ColumnDef<T>>,
    attributes: Array<GetAssetAttributeLookupDto>,
  ) => {
    const mappedColumns = columns.map((leafCol) => {
      const attributeToMap = attributes.find(
        (attribute) => attribute.code === leafCol.id,
      );

      if (attributeToMap) {
        return {
          ...leafCol,
          header: attributeToMap.name,
        };
      }

      return leafCol;
    });

    return mappedColumns;
  };

  const mapGroupColumns = <T>(columns: Array<GroupColumnDef<T>>) => {
    const mappedGroupColumns = columns.map((subGroupCol) => {
      const attributeSubcategory = attributeMap.subcategories.find(
        (subcategory) => subcategory.code === subGroupCol.id,
      );

      if (!attributeSubcategory) {
        return subGroupCol;
      }

      // if this is a header group, look for matching attributes
      if (Array.isArray(subGroupCol.columns)) {
        return {
          ...subGroupCol,
          header: attributeSubcategory.name,
          columns: mergeCustomColumns(
            mapLeafColumns(
              subGroupCol.columns,
              attributeSubcategory.attributes,
            ),
            attributeSubcategory.attributes,
          ),
        };
      }

      return subGroupCol;
    });

    return mappedGroupColumns;
  };

  // ! custom fields not added; needs more clarification
  return columnDefs.map((columnDef) => {
    const groupColumn = columnDef as GroupColumnDef<T>;

    // basic attributes is special; it doesn't have categoryCode as parent
    if (columnDef.id === "BasicAttributes") {
      const basicAttributesSubcategory =
        sharedBasicAttributesMap ??
        attributeMap.subcategories.find(
          (subcategory) =>
            subcategory.code === BASIC_ATTRIBUTES_SUBCATEGORY_CODE,
        );

      if (!basicAttributesSubcategory) {
        return columnDef;
      }

      const basicAttrleafColumns = groupColumn.columns ?? [];

      // basic attributes start off pinned, but check the pinned state just in case
      const allBasicAttrColumnsPinned = basicAttrleafColumns.some(
        (column) =>
          column.enablePinning &&
          (column.meta as Record<string, unknown>)?.isPinned,
      )
        ? {
            enablePinning: true,
            meta: {
              isPinned: true,
            },
          }
        : undefined;

      return {
        ...columnDef,
        columns: mergeCustomColumns(
          mapLeafColumns(
            basicAttrleafColumns,
            basicAttributesSubcategory.attributes ?? [],
          ),
          basicAttributesSubcategory.attributes ?? [],
          allBasicAttrColumnsPinned,
        ),
      };
    }

    if (attributeMap.code === columnDef.id) {
      return {
        ...columnDef,
        header: attributeMap.name,
        columns: mapGroupColumns(
          (groupColumn.columns ?? []) as Array<GroupColumnDef<T>>,
        ),
      };
    }

    return columnDef;
  });
};
